/**
 * Automation schemas — Tier 1 "when X, do Y" rules.
 *
 * MVP: 5 triggers + 5 actions. UI rule builder. Server evaluates on entity
 * mutations + on a cron every 15 min for time-based triggers.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput } from './common';

export const automationTriggerSchema = z.enum([
  'ITEM_OVERDUE',
  'ITEM_DUE_IN_3_DAYS',
  'PROPOSAL_SIGNED',
  'ENGAGEMENT_WON',
  'TIME_ENTRY_LOGGED_OVER_8H',
  'ITEM_DUE_IN_N_DAYS',
  'ITEM_OVERDUE_BY_N_DAYS',
  'SCHEDULE',
  'ITEM_COMPLETED',
  'ITEM_ASSIGNED',
  'PROPOSAL_DECLINED',
  'INVOICE_PAID',
]);

// Trigger tuning knobs. `days` applies to ITEM_DUE_IN_N_DAYS /
// ITEM_OVERDUE_BY_N_DAYS; the rest apply to SCHEDULE (PKT wall-clock).
// Legacy triggers act as aliases: ITEM_DUE_IN_3_DAYS = days 3, ITEM_OVERDUE = days 0.
export const automationTriggerParamsSchema = z.object({
  days: z.number().int().min(1).max(90).optional(),
  frequency: z.enum(['daily', 'weekly', 'monthly']).optional(),
  hour: z.number().int().min(0).max(23).optional(),
  minute: z.number().int().min(0).max(59).optional(),
  dayOfWeek: z.number().int().min(0).max(6).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
});
export type AutomationTriggerParams = z.infer<typeof automationTriggerParamsSchema>;

export const automationActionSchema = z.enum([
  'NOTIFY_ASSIGNEE',
  'NOTIFY_MANAGER',
  'SEND_CLIENT_EMAIL',
  'CREATE_INVOICE',
]);

export const automationConditionSchema = z.object({
  jurisdiction: z.string().optional(),
  clientId: idSchema.optional(),
  serviceType: z.string().optional(),
  assigneeId: idSchema.optional(),
});

export const automationOutputSchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  enabled: z.boolean().default(true),
  trigger: automationTriggerSchema,
  triggerParams: automationTriggerParamsSchema.nullable().default(null),
  conditions: automationConditionSchema,
  action: automationActionSchema,
  actionParams: z.record(z.string(), z.unknown()).default({}),
  lastFiredAt: iso8601Schema.nullable(),
  fireCount: z.number().int().nonnegative().default(0),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type AutomationOutput = z.infer<typeof automationOutputSchema>;

export const createAutomationInputSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  enabled: z.boolean().default(true),
  trigger: automationTriggerSchema,
  triggerParams: automationTriggerParamsSchema.nullable().optional(),
  conditions: automationConditionSchema.optional(),
  action: automationActionSchema,
  actionParams: z.record(z.string(), z.unknown()).default({}),
});
export type CreateAutomationInput = z.infer<typeof createAutomationInputSchema>;

export const updateAutomationInputSchema = createAutomationInputSchema.partial();
export type UpdateAutomationInput = z.infer<typeof updateAutomationInputSchema>;

export const listAutomationsInputSchema = pageInput.extend({
  enabled: z.boolean().optional(),
  trigger: automationTriggerSchema.optional(),
});
export type ListAutomationsInput = z.infer<typeof listAutomationsInputSchema>;

export const automationRunResultSchema = z.enum(['SUCCESS', 'ERROR', 'SKIPPED']);

export const automationRunOutputSchema = z.object({
  id: idSchema,
  automationId: idSchema,
  firedAt: iso8601Schema,
  testRun: z.boolean(),
  entityLabel: z.string().nullable(),
  entityId: z.string().nullable(),
  result: automationRunResultSchema,
  detail: z.string().nullable(),
});
export type AutomationRunOutput = z.infer<typeof automationRunOutputSchema>;

export const testAutomationInputSchema = z.object({
  complianceItemId: idSchema,
});
export type TestAutomationInput = z.infer<typeof testAutomationInputSchema>;